import { useState } from "react";
import {
  Loader2,
  Plus,
  X,
  Save,
  Trash2,
  Zap,
  Power,
  Play,
  ArrowDown,
  Bolt,
} from "lucide-react";
import {
  useAutomatizaciones,
  useCreateAutomatizacion,
  useToggleAutomatizacion,
  useDeleteAutomatizacion,
  useRunInactivity,
  useEtapas,
  useListas,
} from "../api";
import { useMarketingStore } from "../store";
import { MarketingHeader } from "../components/MarketingHeader";
import type {
  AccionTipo,
  Automatizacion,
  AutomatizacionAccion,
  AutomatizacionFormData,
  TriggerTipo,
} from "../types";

const TRIGGERS: Record<TriggerTipo, string> = {
  contacto_creado: "Cuando se crea un contacto / lead",
  contacto_estado: "Cuando un contacto cambia a un estado",
  oportunidad_etapa: "Cuando una oportunidad entra en una etapa",
  oportunidad_ganada: "Cuando se gana una oportunidad",
  inactividad: "Cuando un lead lleva X días inactivo",
};

const ACCIONES: { tipo: AccionTipo; label: string }[] = [
  { tipo: "cambiar_estado", label: "Cambiar estado del contacto" },
  { tipo: "sumar_score", label: "Sumar puntos al lead score" },
  { tipo: "crear_tarea", label: "Crear tarea de seguimiento" },
  { tipo: "crear_actividad", label: "Registrar actividad" },
  { tipo: "registrar_email", label: "Registrar envío de email" },
  { tipo: "anadir_lista", label: "Añadir a una lista" },
  { tipo: "crear_alerta", label: "Crear alerta" },
];

const ESTADOS = ["nuevo", "contactado", "cualificado", "cliente", "descartado"];

export function AutomatizacionesPage() {
  const { emisorId } = useMarketingStore();
  const { data: autos, isLoading } = useAutomatizaciones(emisorId);
  const { data: etapas } = useEtapas(emisorId);
  const { data: listas } = useListas(emisorId);

  const createMut = useCreateAutomatizacion();
  const toggleMut = useToggleAutomatizacion();
  const deleteMut = useDeleteAutomatizacion();
  const inactivityMut = useRunInactivity();

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<AutomatizacionFormData>({
    nombre: "",
    activa: true,
    trigger_tipo: "contacto_creado",
    trigger_config: {},
    acciones: [],
  });

  function reset() {
    setForm({
      nombre: "",
      activa: true,
      trigger_tipo: "contacto_creado",
      trigger_config: {},
      acciones: [],
    });
  }

  function addAccion(tipo: AccionTipo) {
    setForm((f) => ({
      ...f,
      acciones: [...(f.acciones ?? []), { tipo, config: defaultConfig(tipo) }],
    }));
  }
  function updateAccionConfig(idx: number, config: Record<string, unknown>) {
    setForm((f) => ({
      ...f,
      acciones: (f.acciones ?? []).map((a, i) => (i === idx ? { ...a, config } : a)),
    }));
  }
  function removeAccion(idx: number) {
    setForm((f) => ({ ...f, acciones: (f.acciones ?? []).filter((_, i) => i !== idx) }));
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!emisorId || !form.nombre || (form.acciones ?? []).length === 0) return;
    await createMut.mutateAsync({ emisorId, payload: form });
    setCreating(false);
    reset();
  }

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0">
      <MarketingHeader
        title="Marketing"
        subtitle="Automatizaciones: dispara acciones cuando ocurren eventos."
        right={
          <div className="flex gap-2 self-end">
            <button
              onClick={() => emisorId && inactivityMut.mutate(emisorId)}
              disabled={!emisorId || inactivityMut.isPending}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-2 font-bold text-sm transition"
              title="Procesa ahora los triggers de inactividad"
            >
              {inactivityMut.isPending ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
              Ejecutar inactividad
            </button>
            <button
              onClick={() => setCreating(true)}
              disabled={!emisorId}
              className="inline-flex items-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 font-bold text-sm shadow-soft transition disabled:opacity-50"
            >
              <Plus size={16} /> Nueva automatización
            </button>
          </div>
        }
      />

      {inactivityMut.data && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2 text-sm font-bold shrink-0">
          Procesados {inactivityMut.data.procesados} contactos inactivos.
        </div>
      )}

      {isLoading && (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Cargando…
        </div>
      )}

      {autos && (
        <div className="flex-1 min-h-0 overflow-auto">
          {autos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-brand-50 grid place-items-center mb-3">
                <Zap size={28} className="text-brand-700" />
              </div>
              <p className="font-bold text-slate-700">Aún no tienes automatizaciones</p>
              <p className="text-sm text-slate-400 mt-1 max-w-md">
                Crea flujos que se ejecuten solos: dar la bienvenida a nuevos leads, crear tareas
                cuando una oportunidad avanza, reactivar contactos inactivos…
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {autos.map((a: Automatizacion) => (
                <div key={a.id} className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-xl grid place-items-center ${a.activa ? "bg-brand-100 text-brand-700" : "bg-slate-100 text-slate-400"}`}>
                        <Bolt size={20} />
                      </div>
                      <div>
                        <div className="font-black text-slate-900">{a.nombre}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{TRIGGERS[a.trigger_tipo]}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => toggleMut.mutate(a.id)}
                        title={a.activa ? "Pausar" : "Activar"}
                        className={`p-1.5 rounded ${a.activa ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-400 hover:bg-slate-100"}`}
                      >
                        <Power size={16} />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Eliminar "${a.nombre}"?`)) deleteMut.mutate(a.id);
                        }}
                        className="p-1.5 rounded text-slate-300 hover:bg-rose-50 hover:text-rose-600"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Flujo de acciones */}
                  <div className="mt-4 space-y-1.5">
                    {a.acciones.map((ac, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="flex-1 rounded-lg bg-slate-50 border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700">
                          {ACCIONES.find((x) => x.tipo === ac.tipo)?.label ?? ac.tipo}
                          <span className="font-normal text-slate-400 ml-1">{accionResumen(ac)}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-3 flex items-center justify-between text-xs">
                    <span className={`inline-flex px-2 py-0.5 rounded-full font-bold border ${a.activa ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                      {a.activa ? "Activa" : "Pausada"}
                    </span>
                    <span className="text-slate-400">
                      {a.ejecuciones_count} ejecuciones
                      {a.ultima_ejecucion && ` · última ${new Date(a.ultima_ejecucion).toLocaleDateString("es-ES")}`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 max-h-[92vh] overflow-auto">
            <header className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black text-slate-900 flex items-center gap-2">
                <Zap size={20} className="text-brand-700" /> Nueva automatización
              </h2>
              <button type="button" onClick={() => { setCreating(false); reset(); }} className="text-slate-400 hover:text-slate-700">
                <X size={20} />
              </button>
            </header>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Nombre *</label>
                <input
                  required
                  type="text"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  placeholder="ej: Bienvenida a nuevos leads"
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>

              {/* Disparador */}
              <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                <div className="text-xs font-black uppercase tracking-wider text-brand-700 mb-2">
                  ⚡ Cuándo (disparador)
                </div>
                <select
                  value={form.trigger_tipo}
                  onChange={(e) =>
                    setForm({ ...form, trigger_tipo: e.target.value as TriggerTipo, trigger_config: {} })
                  }
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                >
                  {Object.entries(TRIGGERS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>

                {/* Config del trigger */}
                {form.trigger_tipo === "contacto_estado" && (
                  <select
                    value={(form.trigger_config?.estado as string) ?? ""}
                    onChange={(e) => setForm({ ...form, trigger_config: { estado: e.target.value } })}
                    className="w-full mt-2 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="">— estado destino —</option>
                    {ESTADOS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}
                {form.trigger_tipo === "oportunidad_etapa" && (
                  <select
                    value={(form.trigger_config?.etapa_id as number) ?? ""}
                    onChange={(e) => setForm({ ...form, trigger_config: { etapa_id: Number(e.target.value) } })}
                    className="w-full mt-2 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="">— etapa —</option>
                    {etapas?.map((et) => <option key={et.id} value={et.id}>{et.nombre}</option>)}
                  </select>
                )}
                {form.trigger_tipo === "inactividad" && (
                  <div className="mt-2 flex items-center gap-2 text-sm">
                    <span className="text-slate-600">Días sin actividad:</span>
                    <input
                      type="number"
                      min={1}
                      value={(form.trigger_config?.dias as number) ?? 14}
                      onChange={(e) => setForm({ ...form, trigger_config: { dias: Number(e.target.value) } })}
                      className="w-20 rounded-lg border border-slate-300 px-2 py-1 text-sm"
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-center text-slate-300">
                <ArrowDown size={18} />
              </div>

              {/* Acciones */}
              <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-black uppercase tracking-wider text-emerald-700">
                    ✓ Qué hacer (acciones)
                  </div>
                </div>

                {(form.acciones ?? []).length === 0 && (
                  <p className="text-sm text-slate-400 mb-2">Añade al menos una acción.</p>
                )}

                <div className="space-y-2">
                  {(form.acciones ?? []).map((ac, idx) => (
                    <div key={idx} className="rounded-lg border border-slate-200 bg-white p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-bold text-slate-800">
                          {ACCIONES.find((x) => x.tipo === ac.tipo)?.label}
                        </span>
                        <button type="button" onClick={() => removeAccion(idx)} className="text-slate-300 hover:text-rose-600">
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <AccionConfig
                        accion={ac}
                        etapas={listas}
                        onChange={(cfg) => updateAccionConfig(idx, cfg)}
                      />
                    </div>
                  ))}
                </div>

                <div className="mt-3">
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value) addAccion(e.target.value as AccionTipo);
                    }}
                    className="w-full rounded-lg border border-dashed border-slate-300 bg-white px-3 py-2 text-sm font-bold text-slate-600"
                  >
                    <option value="">+ Añadir acción…</option>
                    {ACCIONES.map((a) => <option key={a.tipo} value={a.tipo}>{a.label}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => { setCreating(false); reset(); }} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-bold hover:bg-slate-50">
                Cancelar
              </button>
              <button
                type="submit"
                disabled={createMut.isPending || (form.acciones ?? []).length === 0}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
              >
                <Save size={14} /> Crear automatización
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function defaultConfig(tipo: AccionTipo): Record<string, unknown> {
  switch (tipo) {
    case "cambiar_estado": return { estado: "contactado" };
    case "sumar_score": return { puntos: 10 };
    case "crear_tarea": return { asunto: "Hacer seguimiento", dias: 1 };
    case "crear_actividad": return { tipo: "nota", asunto: "Actividad automática" };
    case "registrar_email": return { asunto: "Email automático" };
    case "anadir_lista": return { lista_id: 0 };
    case "crear_alerta": return { titulo: "Alerta", severidad: "info" };
    default: return {};
  }
}

function accionResumen(ac: AutomatizacionAccion): string {
  const c = ac.config;
  switch (ac.tipo) {
    case "cambiar_estado": return `→ ${c.estado}`;
    case "sumar_score": return `+${c.puntos} pts`;
    case "crear_tarea": return `"${c.asunto}" en ${c.dias}d`;
    case "crear_actividad": return `"${c.asunto}"`;
    case "crear_alerta": return `"${c.titulo}"`;
    default: return "";
  }
}

function AccionConfig({
  accion,
  etapas: listas,
  onChange,
}: {
  accion: AutomatizacionAccion;
  etapas?: { id: number; nombre: string }[];
  onChange: (cfg: Record<string, unknown>) => void;
}) {
  const c = accion.config;
  const set = (patch: Record<string, unknown>) => onChange({ ...c, ...patch });

  switch (accion.tipo) {
    case "cambiar_estado":
      return (
        <select value={c.estado as string} onChange={(e) => set({ estado: e.target.value })} className="w-full rounded border border-slate-200 px-2 py-1 text-sm">
          {ESTADOS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      );
    case "sumar_score":
      return (
        <input type="number" value={c.puntos as number} onChange={(e) => set({ puntos: Number(e.target.value) })} className="w-full rounded border border-slate-200 px-2 py-1 text-sm" placeholder="puntos" />
      );
    case "crear_tarea":
      return (
        <div className="flex gap-2">
          <input type="text" value={c.asunto as string} onChange={(e) => set({ asunto: e.target.value })} className="flex-1 rounded border border-slate-200 px-2 py-1 text-sm" placeholder="asunto" />
          <input type="number" value={c.dias as number} onChange={(e) => set({ dias: Number(e.target.value) })} className="w-20 rounded border border-slate-200 px-2 py-1 text-sm" placeholder="días" />
        </div>
      );
    case "crear_actividad":
      return (
        <input type="text" value={c.asunto as string} onChange={(e) => set({ asunto: e.target.value })} className="w-full rounded border border-slate-200 px-2 py-1 text-sm" placeholder="asunto" />
      );
    case "registrar_email":
      return (
        <input type="text" value={(c.asunto as string) ?? ""} onChange={(e) => set({ asunto: e.target.value })} className="w-full rounded border border-slate-200 px-2 py-1 text-sm" placeholder="asunto del email" />
      );
    case "anadir_lista":
      return (
        <select value={(c.lista_id as number) ?? 0} onChange={(e) => set({ lista_id: Number(e.target.value) })} className="w-full rounded border border-slate-200 px-2 py-1 text-sm">
          <option value={0}>— elegir lista —</option>
          {listas?.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
        </select>
      );
    case "crear_alerta":
      return (
        <div className="flex gap-2">
          <input type="text" value={c.titulo as string} onChange={(e) => set({ titulo: e.target.value })} className="flex-1 rounded border border-slate-200 px-2 py-1 text-sm" placeholder="título" />
          <select value={c.severidad as string} onChange={(e) => set({ severidad: e.target.value })} className="rounded border border-slate-200 px-2 py-1 text-sm">
            <option value="info">Info</option>
            <option value="warning">Aviso</option>
            <option value="hot">Caliente</option>
          </select>
        </div>
      );
    default:
      return null;
  }
}
